import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, Filter, Star, MapPin, Clock, Phone, Video, Calendar, Award } from 'lucide-react';
import { API_BASE_URL } from '../config/api';

const DoctorDirectory = () => {
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    specialization: '',
    availability: '',
    rating: '',
    experience: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 12
  });
  
  // Image loading state - must be at top level
  const backgroundImageUrl = `${API_BASE_URL}/uploads/backgrounds/doctor.jpg`;
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  
  // Preload image to check if it's accessible
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      setImageLoaded(true);
      setImageError(false);
      console.log('Doctor directory background image loaded successfully:', backgroundImageUrl);
    };
    img.onerror = () => {
      setImageError(true);
      setImageLoaded(false);
      console.error('Failed to load doctor directory background image:', backgroundImageUrl);
    };
    img.src = backgroundImageUrl;
  }, [backgroundImageUrl]);

  useEffect(() => {
    fetchDoctors();
  }, [filters, pagination.currentPage]);

  const fetchDoctors = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams({
        page: pagination.currentPage,
        limit: pagination.itemsPerPage,
        ...filters
      });

      const response = await fetch(`${API_BASE_URL}/api/users/doctors?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setDoctors(data.doctors);
        setPagination(data.pagination);
      }
    } catch (error) {
      console.error('Error fetching doctors:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    // Implement search functionality
    console.log('Searching for:', searchTerm);
  };

  const getSpecializationColor = (specialization) => {
    const colors = {
      'Cardiology': 'bg-red-100 text-red-800',
      'Dermatology': 'bg-blue-100 text-blue-800',
      'Neurology': 'bg-purple-100 text-purple-800',
      'Orthopedics': 'bg-green-100 text-green-800',
      'Pediatrics': 'bg-yellow-100 text-yellow-800',
      'Psychiatry': 'bg-indigo-100 text-indigo-800',
      'Oncology': 'bg-pink-100 text-pink-800',
      'General Medicine': 'bg-gray-100 text-gray-800'
    };
    return colors[specialization] || 'bg-gray-100 text-gray-800';
  };

  const formatExperience = (years) => {
    if (years === 1) return '1 year';
    return `${years} years`;
  };

  const getAvailabilityStatus = (availability) => {
    if (!availability || availability.length === 0) return 'Not specified';
    
    const today = new Date().toLocaleDateString('en-US', { weekday: 'lowercase' });
    const todaySchedule = availability.find(schedule => schedule.day === today);
    
    if (todaySchedule && todaySchedule.slots.some(slot => slot.isAvailable)) {
      return 'Available today';
    }
    return 'Check availability';
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <Star key={i} className="h-4 w-4 fill-current text-yellow-400" />
      );
    }

    if (hasHalfStar) {
      stars.push(
        <Star key="half" className="h-4 w-4 fill-current text-yellow-400" />
      );
    }

    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(
        <Star key={`empty-${i}`} className="h-4 w-4 text-gray-300" />
      );
    }

    return stars;
  };

  // Fallback background color
  const backgroundStyle = imageError 
    ? { backgroundColor: '#3b82f6', minHeight: '300px' }
    : {
        backgroundImage: imageLoaded 
          ? `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url(${backgroundImageUrl})`
          : 'linear-gradient(rgba(59, 130, 246, 0.6), rgba(59, 130, 246, 0.6))',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        minHeight: '300px'
      };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section with Background */}
      <div 
        className="relative py-20 mb-8"
        style={backgroundStyle}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Find Your Healthcare Provider
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto">
              Connect with qualified doctors and specialists for personalized healthcare consultations
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8">

        {/* Search and Filters */}
        <div className="card mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            {/* Search */}
            <form onSubmit={handleSearch} className="flex-1 max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search doctors by name or specialization..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="input-field pl-10 w-full"
                />
              </div>
            </form>

            {/* Filters */}
            <div className="flex flex-wrap gap-4">
              <select
                value={filters.specialization}
                onChange={(e) => handleFilterChange('specialization', e.target.value)}
                className="input-field min-w-[160px]"
              >
                <option value="">All Specializations</option>
                <option value="Cardiology">Cardiology</option>
                <option value="Dermatology">Dermatology</option>
                <option value="Neurology">Neurology</option>
                <option value="Orthopedics">Orthopedics</option>
                <option value="Pediatrics">Pediatrics</option>
                <option value="Psychiatry">Psychiatry</option>
                <option value="Oncology">Oncology</option>
                <option value="General Medicine">General Medicine</option>
              </select>

              <select
                value={filters.availability}
                onChange={(e) => handleFilterChange('availability', e.target.value)}
                className="input-field min-w-[140px]"
              >
                <option value="">All Availability</option>
                <option value="available">Available Today</option>
                <option value="weekdays">Weekdays</option>
                <option value="weekends">Weekends</option>
              </select>

              <select
                value={filters.rating}
                onChange={(e) => handleFilterChange('rating', e.target.value)}
                className="input-field min-w-[120px]"
              >
                <option value="">All Ratings</option>
                <option value="4">4+ Stars</option>
                <option value="3">3+ Stars</option>
                <option value="2">2+ Stars</option>
              </select>

              <select
                value={filters.experience}
                onChange={(e) => handleFilterChange('experience', e.target.value)}
                className="input-field min-w-[140px]"
              >
                <option value="">All Experience</option>
                <option value="10">10+ Years</option>
                <option value="5">5+ Years</option>
                <option value="2">2+ Years</option>
              </select>
            </div>
          </div>
        </div>

        {/* Doctors Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
          {doctors.map((doctor) => (
            <div key={doctor._id} className="card hover:shadow-lg transition-shadow duration-200">
              {/* Doctor Header */}
              <div className="text-center mb-4">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-primary-100 rounded-full mb-3">
                  <span className="text-2xl font-bold text-primary-600">
                    {doctor.name.charAt(0)}
                  </span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                  {doctor.name}
                </h3>
                <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getSpecializationColor(doctor.doctorProfile?.specialization)}`}>
                  {doctor.doctorProfile?.specialization || 'General Medicine'}
                </span>
              </div>

              {/* Doctor Info */}
              <div className="space-y-3 mb-4">
                <div className="flex items-center text-sm text-gray-600">
                  <Award className="h-4 w-4 mr-2 text-primary-500" />
                  <span>{formatExperience(doctor.doctorProfile?.experience || 0)} experience</span>
                </div>
                
                {doctor.doctorProfile?.education && doctor.doctorProfile.education.length > 0 && (
                  <div className="text-sm text-gray-600">
                    <div className="font-medium text-gray-700 mb-1">Education:</div>
                    {doctor.doctorProfile.education.slice(0, 2).map((edu, index) => (
                      <div key={index} className="text-xs">
                        {edu.degree} - {edu.institution} ({edu.year})
                      </div>
                    ))}
                  </div>
                )}

                {doctor.doctorProfile?.certifications && doctor.doctorProfile.certifications.length > 0 && (
                  <div className="text-sm text-gray-600">
                    <div className="font-medium text-gray-700 mb-1">Certifications:</div>
                    <div className="text-xs">
                      {doctor.doctorProfile.certifications.slice(0, 2).join(', ')}
                    </div>
                  </div>
                )}
              </div>

              {/* Rating */}
              <div className="flex items-center justify-center mb-4">
                <div className="flex items-center space-x-1">
                  {renderStars(doctor.ratings?.average || 0)}
                </div>
                <span className="ml-2 text-sm text-gray-600">
                  ({doctor.ratings?.count || 0} reviews)
                </span>
              </div>

              {/* Availability */}
              <div className="text-center mb-4">
                <div className="text-sm text-gray-600 mb-1">
                  {getAvailabilityStatus(doctor.doctorProfile?.availability)}
                </div>
                <div className="text-xs text-gray-500">
                  Consultation Fee: ${doctor.doctorProfile?.consultationFee || 'Contact'}
                </div>
              </div>

              {/* Actions */}
              <div className="flex space-x-2">
                <Link
                  to={`/appointments/new?doctorId=${doctor._id}`}
                  className="flex-1 btn-primary text-center text-sm py-2"
                >
                  Book Appointment
                </Link>
                <Link
                  to={`/doctors/${doctor._id}`}
                  className="btn-outline text-sm py-2 px-3"
                >
                  View Profile
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* No Results */}
        {doctors.length === 0 && (
          <div className="text-center py-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
              <Search className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No doctors found</h3>
            <p className="text-gray-600 mb-4">
              {searchTerm || Object.values(filters).some(f => f) 
                ? 'Try adjusting your search or filters'
                : 'No doctors are currently available'
              }
            </p>
            {!searchTerm && !Object.values(filters).some(f => f) && (
              <button
                onClick={() => {
                  setFilters({ specialization: '', availability: '', rating: '', experience: '' });
                  setSearchTerm('');
                }}
                className="btn-primary"
              >
                Clear Filters
              </button>
            )}
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-center space-x-2">
            <button
              onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage - 1 }))}
              disabled={pagination.currentPage === 1}
              className="px-4 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            
            <span className="px-4 py-2 text-sm font-medium text-gray-700">
              Page {pagination.currentPage} of {pagination.totalPages}
            </span>
            
            <button
              onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage + 1 }))}
              disabled={pagination.currentPage === pagination.totalPages}
              className="px-4 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        )}

        {/* Features Section */}
        <div className="mt-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Why Choose Our Doctors?
            </h2>
            <p className="text-xl text-gray-600">
              Professional healthcare providers committed to your well-being
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full mb-4">
                <Award className="h-8 w-8 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Verified Credentials</h3>
              <p className="text-gray-600">
                All doctors are verified with proper licenses and certifications
              </p>
            </div>
            
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-secondary-100 rounded-full mb-4">
                <Clock className="h-8 w-8 text-secondary-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Flexible Scheduling</h3>
              <p className="text-gray-600">
                Book appointments at times that work best for your schedule
              </p>
            </div>
            
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
                <Video className="h-8 w-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Multiple Consultation Modes</h3>
              <p className="text-gray-600">
                Choose between video, audio, or chat consultations
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DoctorDirectory;
